/**
 * =============================================================================
 * imageFallback.ts
 * -----------------------------------------------------------------------------
 * Installazione di un listener globale che intercetta gli errori di caricamento
 * delle immagini (`<img>` che falliscono per 404/401/CORS/...) e li sostituisce
 * con un segnaposto same-origin. Logga sempre l'evento per facilitare il
 * debug in produzione.
 *
 * Catturare l'evento "error" a livello di document, in fase di capture, è
 * necessario perché gli eventi di error sulle risorse NON fanno bubbling:
 * il flag { capture: true } è obbligatorio.
 *
 * Pensato per coprire anche le immagini iniettate via `dangerouslySetInnerHTML`
 * dai componenti che renderizzano contenuti editoriali (ProcessedContent &
 * simili), per cui sarebbe scomodo avvolgere ogni `<img>` in un componente
 * React dedicato.
 * =============================================================================
 */

const PLACEHOLDER_SRC = "/placeholder.svg";
const MARK_ATTR = "data-image-fallback-applied";

/**
 * Installa il listener globale (idempotente).
 * Da chiamare una sola volta all'avvio dell'app (vedi main.tsx).
 */
export function installImageErrorFallback(): void {
  if (typeof document === "undefined") return;
  if ((window as unknown as { __imgFallbackInstalled?: boolean }).__imgFallbackInstalled) {
    return;
  }
  (window as unknown as { __imgFallbackInstalled: boolean }).__imgFallbackInstalled = true;

  document.addEventListener(
    "error",
    (event) => {
      const target = event.target as HTMLElement | null;
      if (!target || target.tagName !== "IMG") return;
      const img = target as HTMLImageElement;

      // Evita loop: se siamo già sul placeholder o l'abbiamo già marcato, esci.
      if (img.getAttribute(MARK_ATTR) === "true") return;
      if (img.src.endsWith(PLACEHOLDER_SRC)) return;

      const failedSrc = img.currentSrc || img.src;
      // eslint-disable-next-line no-console
      console.warn("[image-fallback] immagine non caricata, applico placeholder", {
        src: failedSrc,
        alt: img.alt || null,
        url: window.location.href,
      });

      img.setAttribute(MARK_ATTR, "true");
      img.setAttribute("data-original-src", failedSrc);
      img.src = PLACEHOLDER_SRC;
      if (!img.title) img.title = "Immagine non disponibile";
    },
    true, // capture phase: indispensabile per eventi di risorsa
  );
}
